import { ChevronRight, PlusSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FocusTag } from '../../atoms/FocusTag';
import { RelatedContextTag } from '../../atoms/RelatedContextTag';
import { TagChip } from '../../atoms/TagChip';
import { useHoverWithDebounce, usePopoverPlacement } from '../../hooks';
import { cn } from '../../lib/utils';

export interface BreadcrumbRelatedAction {
  id: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}

export interface BreadcrumbRelatedTag {
  id: string;
  label: string;
  count: number;
  isHighlighted?: boolean;
  /** Identity-axis label for the popover's chevron header. */
  fromLabel: string;
  /** Data-context-axis label for the popover's chevron header. */
  toLabel: string;
  /** Two actions (Focus / Show Access Events) shown in the hover popover. */
  actions: readonly BreadcrumbRelatedAction[];
}

export interface BreadcrumbTagRowProps {
  /** Identity-axis state. */
  identityFocus: { id: string; label: string } | null;
  /** Data-context-axis state. Shape varies by view (default vs drill-down). */
  dataFocus:
    | { kind: 'static' }
    | { kind: 'focus'; id: string; label: string }
    | {
        kind: 'drilldown';
        /**
         * The drilled tiers, department first, each rendered as its own tag.
         * Length is whatever the warehouse published beneath the department,
         * so a flat drill is a trail of one.
         */
        trail: readonly { id: string; label: string }[];
        /** When non-null, renders a further focus tag for the focused row of the deepest tier. */
        focusedSubCategory: { id: string; label: string } | null;
      };
  /** Tags shown alongside the identity focus. Empty when nothing is focused. */
  identityRelated: readonly BreadcrumbRelatedTag[];
  /** Tags shown alongside the data focus. Empty when nothing is focused / drilled. */
  dataRelated: readonly BreadcrumbRelatedTag[];
  formatNumber: (value: number) => string;
  onDismissIdentityFocus: () => void;
  /** Fired when dismissing the department-level data focus (focus or drill-down). */
  onDismissDataFocus: () => void;
  /** Fired when dismissing only the sub-category focus inside drill-down (stays in drill-down). */
  onDismissSubCategoryFocus?: () => void;
  /**
   * Fired when dismissing a drilled tier. The argument is the trail length to
   * keep, so dismissing the department (index 0) leaves the drill entirely and
   * dismissing a deeper tier returns to the one above it.
   */
  onDismissDrillLevel?: (toDepth: number) => void;
  /** Fired when the user clicks the static "All Departments" chip. */
  onClickAllDepartments?: () => void;
  /** Fired when the user clicks the static "All Data" chip (or the back-link in drill-down). */
  onClickAllData?: () => void;
  className?: string;
}

const POPOVER_CLOSE_GRACE_MS = 150;

interface BreadcrumbActionPopoverProps {
  fromLabel: string;
  toLabel: string;
  actions: readonly BreadcrumbRelatedAction[];
  anchor: HTMLElement;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function BreadcrumbActionPopover({
  fromLabel,
  toLabel,
  actions,
  anchor,
  onMouseEnter,
  onMouseLeave,
}: BreadcrumbActionPopoverProps) {
  const { ref, style } = usePopoverPlacement(anchor);

  return (
    <div
      ref={ref}
      data-testid="breadcrumb-action-popover"
      role="menu"
      aria-label={`${fromLabel} > ${toLabel}`}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Figma `5:20399`: same 217/20px/28px menu geometry as RowActionPopover.
      className="flex min-w-[217px] flex-col overflow-hidden rounded-lg border border-grey-200 bg-white p-5 shadow-lg"
    >
      <div className="flex items-center gap-1">
        <span className="text-[13px] font-medium leading-[14px] text-dawn-black">{fromLabel}</span>
        <ChevronRight className="h-3 w-3 shrink-0 text-grey-400" aria-hidden="true" />
        <span className="text-[13px] font-medium leading-[14px] text-dawn-black">{toLabel}</span>
      </div>
      <div className="my-4 h-px bg-grey-100" aria-hidden="true" />
      <div className="flex flex-col gap-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              onClick={action.onClick}
              className="-mx-5 -my-1 flex items-center gap-1 px-5 py-1 text-left text-[12px] leading-3 text-dawn-black hover:bg-grey-50"
            >
              <Icon className="h-3 w-3 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The element a popover hangs from, set at once and cleared after a grace
 * window, so the pointer can cross the gap into the popover.
 */
function useGraceAnchor() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setAnchor(null);
      closeTimerRef.current = null;
    }, POPOVER_CLOSE_GRACE_MS);
  }, [cancelClose]);

  const open = useCallback(
    (el: HTMLElement | null) => {
      cancelClose();
      setAnchor(el);
    },
    [cancelClose]
  );

  return { anchor, open, cancelClose, scheduleClose };
}

interface RelatedTagWithPopoverProps {
  tag: BreadcrumbRelatedTag;
  formatNumber: (value: number) => string;
}

function RelatedTagWithPopover({ tag, formatNumber }: RelatedTagWithPopoverProps) {
  const tagRef = useRef<HTMLDivElement>(null);
  const hover = useHoverWithDebounce();
  const { anchor, open, cancelClose, scheduleClose } = useGraceAnchor();

  useEffect(() => {
    if (hover.isHovered && tagRef.current) open(tagRef.current);
  }, [hover.isHovered, open]);

  return (
    <div className="relative shrink-0">
      <RelatedContextTag
        ref={tagRef}
        label={tag.label}
        count={formatNumber(tag.count)}
        isHighlighted={tag.isHighlighted}
        onMouseEnter={() => {
          cancelClose();
          hover.onMouseEnter();
        }}
        onMouseLeave={() => {
          hover.onMouseLeave();
          scheduleClose();
        }}
      />
      {anchor && (
        <BreadcrumbActionPopover
          fromLabel={tag.fromLabel}
          toLabel={tag.toLabel}
          actions={tag.actions}
          anchor={anchor}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
    </div>
  );
}

/** `gap-2` between related tags. */
const TAG_GAP_PX = 8;
/** Room kept for the "+N" chip once tags overflow (`TagChip` sm, up to "+99"). */
const OVERFLOW_CHIP_PX = 32;

/**
 * How many tags, in order, fit in `available` px: all of them when they fit
 * outright, otherwise as many as still leave room for the "+N" chip.
 */
function countFittingTags(widths: readonly number[], available: number): number {
  const total = widths.reduce((sum, width) => sum + width, 0) + TAG_GAP_PX * (widths.length - 1);
  if (total <= available) return widths.length;
  let used = OVERFLOW_CHIP_PX;
  let count = 0;
  while (count < widths.length && used + widths[count]! + TAG_GAP_PX <= available) {
    used += widths[count]! + TAG_GAP_PX;
    count += 1;
  }
  return count;
}

interface RelatedTagsProps {
  tags: readonly BreadcrumbRelatedTag[];
  formatNumber: (value: number) => string;
}

/**
 * "+N" for the related tags that don't fit the breadcrumb line, in the
 * `TopicListPopover` chip and panel chrome. The panel holds the hidden tags
 * themselves, so each keeps its own Focus / Show Access Events menu.
 */
function OverflowTagChip({ tags, formatNumber }: RelatedTagsProps) {
  const chipRef = useRef<HTMLSpanElement>(null);
  const { anchor, open, scheduleClose } = useGraceAnchor();

  return (
    <div
      className="shrink-0"
      onMouseEnter={() => open(chipRef.current)}
      onMouseLeave={scheduleClose}
      onFocus={() => open(chipRef.current)}
      onBlur={scheduleClose}
    >
      <span
        ref={chipRef}
        data-testid="breadcrumb-overflow-chip"
        aria-label={`plus ${tags.length} more`}
        tabIndex={0}
        className="block cursor-default outline-none focus-visible:ring-2 focus-visible:ring-dawn-orange focus-visible:ring-offset-1"
      >
        <TagChip size="sm" state="subtle">
          {`+${tags.length}`}
        </TagChip>
      </span>
      {anchor && <OverflowTagPanel anchor={anchor} tags={tags} formatNumber={formatNumber} />}
    </div>
  );
}

function OverflowTagPanel({
  anchor,
  tags,
  formatNumber,
}: RelatedTagsProps & { anchor: HTMLElement }) {
  const { ref, style } = usePopoverPlacement(anchor);
  return (
    <div
      ref={ref}
      data-testid="breadcrumb-overflow-popover"
      style={style}
      // Bounded and scrolling (the `FilterDropdown` list bound), so a long list
      // never runs past the card. The tags' own menus are `position: fixed`, so
      // the scroller doesn't clip them.
      className="flex max-h-72 flex-col gap-2 overflow-y-auto rounded-[4px] border border-grey-200 bg-dawn-white p-3 shadow-md"
    >
      {tags.map((tag) => (
        <RelatedTagWithPopover key={tag.id} tag={tag} formatNumber={formatNumber} />
      ))}
    </div>
  );
}

/**
 * The related tags on the breadcrumb's one line. Tags that don't fit collapse
 * into a "+N" chip, so focusing a row with many connections never wraps the
 * breadcrumb and shifts the chart under the pointer.
 *
 * The list is `flex-1` (basis 0): it takes only the space the focus chips
 * leave, so its tags never push them, and it never drops below the "+N" chip.
 * When the focus chips alone overflow, they truncate instead (`FocusTag`).
 *
 * A tag's width doesn't depend on the row's (tags neither shrink nor wrap), so
 * each tag set is measured once, rendered in full — the row clips the overflow
 * for that one layout pass — and a resize only redoes the sum.
 */
function RelatedTagList({ tags, formatNumber }: RelatedTagsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  // The labels carry the focus label, which moves where the list starts.
  const tagsKey = JSON.stringify(tags.map((t) => [t.id, t.label, t.count, t.fromLabel, t.toLabel]));
  const [fit, setFit] = useState<{ key: string; count: number } | null>(null);
  const shown = fit?.key === tagsKey ? fit.count : tags.length;

  useLayoutEffect(() => {
    const list = listRef.current;
    const row = list?.parentElement;
    if (!list || !row) return;
    const widths = Array.from(list.children, (el) => (el as HTMLElement).offsetWidth);
    const refit = () => {
      const rowRect = row.getBoundingClientRect();
      // No layout to measure against (jsdom, a display:none ancestor): show all.
      const count =
        rowRect.width === 0
          ? widths.length
          : countFittingTags(widths, rowRect.right - list.getBoundingClientRect().left);
      setFit((prev) =>
        prev?.key === tagsKey && prev.count === count ? prev : { key: tagsKey, count }
      );
    };
    refit();
    const observer = new ResizeObserver(refit);
    observer.observe(row);
    return () => observer.disconnect();
  }, [tagsKey]);

  if (tags.length === 0) return null;
  const hidden = tags.slice(shown);
  return (
    <div
      ref={listRef}
      className="ml-3 flex flex-1 items-center gap-2"
      style={{ minWidth: OVERFLOW_CHIP_PX }}
    >
      {tags.slice(0, shown).map((tag) => (
        <RelatedTagWithPopover key={tag.id} tag={tag} formatNumber={formatNumber} />
      ))}
      {hidden.length > 0 && <OverflowTagChip tags={hidden} formatNumber={formatNumber} />}
    </div>
  );
}

/**
 * One fixed 28px line in every state, so focusing never moves the chart.
 * `whitespace-nowrap` keeps each chip at its natural width instead of letting
 * flex squeeze its text onto a second line when the tags overflow.
 */
const ROW_CLASS = 'flex h-7 items-center overflow-hidden whitespace-nowrap';

export function BreadcrumbTagRow({
  identityFocus,
  dataFocus,
  identityRelated,
  dataRelated,
  formatNumber,
  onDismissIdentityFocus,
  onDismissDataFocus,
  onDismissSubCategoryFocus,
  onDismissDrillLevel,
  onClickAllDepartments,
  onClickAllData,
  className,
}: BreadcrumbTagRowProps) {
  const rootClass = cn(ROW_CLASS, 'gap-2', className);
  const chevron = <ChevronRight className="h-3 w-3 shrink-0 text-grey-400" aria-hidden="true" />;

  if (identityFocus) {
    return (
      <div data-testid="breadcrumb-tag-row" className={rootClass}>
        <TagChip
          state="subtle"
          weight="regular"
          className={cn(onClickAllDepartments && 'cursor-pointer')}
        >
          <button
            type="button"
            onClick={onClickAllDepartments}
            className="text-[13px] leading-[1.3] text-dawn-black"
          >
            All Departments
          </button>
        </TagChip>
        {chevron}
        <FocusTag onDismiss={onDismissIdentityFocus}>{identityFocus.label}</FocusTag>
        <RelatedTagList tags={identityRelated} formatNumber={formatNumber} />
      </div>
    );
  }

  if (dataFocus.kind === 'focus') {
    return (
      <div data-testid="breadcrumb-tag-row" className={rootClass}>
        <TagChip state="subtle" weight="regular" className={cn(onClickAllData && 'cursor-pointer')}>
          <button
            type="button"
            onClick={onClickAllData}
            className="text-[13px] leading-[1.3] text-dawn-black"
          >
            All Data
          </button>
        </TagChip>
        {chevron}
        <FocusTag onDismiss={onDismissDataFocus}>{dataFocus.label}</FocusTag>
        <RelatedTagList tags={dataRelated} formatNumber={formatNumber} />
      </div>
    );
  }

  if (dataFocus.kind === 'drilldown') {
    return (
      <div data-testid="breadcrumb-tag-row" className={rootClass}>
        <TagChip state="subtle" weight="regular" className="cursor-pointer">
          <button
            type="button"
            data-testid="all-data-back-link"
            onClick={onDismissDataFocus}
            className="text-[13px] leading-[1.3] text-dawn-black"
          >
            All Data
          </button>
        </TagChip>
        {dataFocus.trail.map((step, depth) => (
          <Fragment key={step.id}>
            {chevron}
            <FocusTag
              icon={depth === 0 ? PlusSquare : undefined}
              onDismiss={
                onDismissDrillLevel ? () => onDismissDrillLevel(depth) : onDismissDataFocus
              }
            >
              {step.label}
            </FocusTag>
          </Fragment>
        ))}
        {dataFocus.focusedSubCategory && (
          <>
            {chevron}
            <FocusTag onDismiss={onDismissSubCategoryFocus ?? (() => {})}>
              {dataFocus.focusedSubCategory.label}
            </FocusTag>
          </>
        )}
        <RelatedTagList tags={dataRelated} formatNumber={formatNumber} />
      </div>
    );
  }

  return (
    <div data-testid="breadcrumb-tag-row" className={cn(ROW_CLASS, 'gap-5', className)}>
      <TagChip
        state="subtle"
        weight="regular"
        className={cn(onClickAllDepartments && 'cursor-pointer')}
      >
        <button
          type="button"
          onClick={onClickAllDepartments}
          className="text-[13px] leading-[1.3] text-dawn-black"
        >
          All Departments
        </button>
      </TagChip>
      <TagChip state="subtle" weight="regular" className={cn(onClickAllData && 'cursor-pointer')}>
        <button
          type="button"
          onClick={onClickAllData}
          className="text-[13px] leading-[1.3] text-dawn-black"
        >
          All Data
        </button>
      </TagChip>
    </div>
  );
}

BreadcrumbTagRow.displayName = 'BreadcrumbTagRow';
